import { Injectable, OnDestroy } from "@angular/core";
import { AuthenticationService } from "./authentication.service";
import * as signalR from "@microsoft/signalr";
import { BehaviorSubject, Subscription, timer } from "rxjs";
import { environment } from "src/environments/environment";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";

interface HubConnectionError {
  message: string;
  code?: number;
  details?: unknown;
}

@Injectable({
  providedIn: "root"
})
export class SignalRService implements OnDestroy {
  private _connections: {
    [key: string]: BehaviorSubject<HubConnection>;
  } = {};

  private _subscriptions: {
    [key: string]: Subscription;
  } = {};

  private _reconnectTimeouts: {
    [key: string]: NodeJS.Timeout;
  } = {};

  private readonly MAX_RETRY_ATTEMPTS = null;
  private readonly INITIAL_RETRY_DELAY = 2000;
  private readonly MAX_RETRY_DELAY = 30000;

  constructor(private _authenticationService: AuthenticationService) {}

  public get commonHub(): BehaviorSubject<HubConnection> {
    return this.getConnection("common");
  }

  public get commonHubValue(): HubConnection {
    return this._connections["common"]?.value;
  }

  public isHubConnected(connection: HubConnection): boolean {
    return connection?.state === HubConnectionState.Connected;
  }

  public isHubDisconnected(connection: HubConnection): boolean {
    return connection?.state === HubConnectionState.Disconnected;
  }

  public isHubConnecting(connection: HubConnection): boolean {
    return connection?.state === HubConnectionState.Connecting;
  }

  public isHubReconnecting(connection: HubConnection): boolean {
    return connection?.state === HubConnectionState.Reconnecting;
  }

  public getConnection(hub: string): BehaviorSubject<HubConnection> {
    if (!this._connections[hub]) {
      this._connections[hub] = new BehaviorSubject<HubConnection>(null);
      this._setupHubConnection(hub);
    }
    return this._connections[hub];
  }

  private _setupHubConnection(hub: string): void {
    if (this._subscriptions[hub]) {
      this._subscriptions[hub].unsubscribe();
    }

    this._subscriptions[hub] = this._authenticationService.jwt.subscribe(
      (jwt) => {
        const isLoggedIn = this._authenticationService.isLoggedIn;
        const connection = this._connections[hub].value;

        if (!isLoggedIn) {
          this._handleDisconnection(hub, connection);
        } else {
          this._handleConnection(hub, connection);
        }
      }
    );
  }

  private _handleDisconnection(hub: string, connection: HubConnection): void {
    if (this.isHubConnected(connection)) {
      connection
        .stop()
        .then(() => {
          console.log(`SignalR connection in hub ${hub} stopped`);
        })
        .catch((err) => {
          console.error("Error stopping connection", err);
        });
    }
  }

  private _handleConnection(hub: string, connection: HubConnection): void {
    if (this.isHubDisconnected(connection)) {
      this._startConnection(hub, connection);
    } else {
      this._createHub(hub);
    }
  }

  private _startConnection(hub: string, connection: HubConnection): void {
    connection
      .start()
      .then(() => {
        console.log(`SignalR connection in hub ${hub} started`);
      })
      .catch((err) => {
        console.error("Error starting connection", err);
        this._scheduleReconnect(hub);
      });
  }

  private _createConnection(
    hub: string,
    connected: (connection: HubConnection) => void,
    failed?: (err: HubConnectionError) => void
  ): Promise<HubConnection> {
    const isLoggedIn = this._authenticationService.isLoggedIn;

    if (!isLoggedIn) {
      return Promise.reject(new Error("User not logged in"));
    }

    return new Promise<HubConnection>((resolve, reject) => {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${environment.apiUrl}hub/${hub}`)
        .withAutomaticReconnect([0, 2000, 5000, 10000, 20000]) // Exponential backoff
        .build();

      this._setupConnectionHandlers(hub, connection, connected, failed, resolve, reject);
    });
  }

  private _setupConnectionHandlers(
    hub: string,
    connection: HubConnection,
    connected: (connection: HubConnection) => void,
    failed: (err: HubConnectionError) => void,
    resolve: (value: HubConnection) => void,
    reject: (reason: any) => void
  ): void {
    connection.onclose((error) => {
      if (!this._authenticationService.isLoggedIn) {
        return;
      }

      if (error) {
        console.error(
          `SignalR connection in hub ${hub} closed with error, attempting to reconnect`,
          error
        );
      } else {
        console.log(
          `SignalR connection in hub ${hub} closed, attempting to reconnect`
        );
      }

      this._scheduleReconnect(hub);
    });

    connection
      .start()
      .then(() => {
        connected(connection);
        resolve(connection);
      })
      .catch((err) => {
        if (failed) {
          failed(err);
        }
        reject(err);
      });
  }

  private _createHub(hub: string): Promise<BehaviorSubject<HubConnection>> {
    return this._createConnection(
      hub,
      (connection) => {
        this._connections[hub].next(connection);
      },
      (err) => {
        console.error(
          `Error creating SignalR connection in hub ${hub}, attempting to reconnect`,
          err
        );
        this._scheduleReconnect(hub);
      }
    ).then(() => this._connections[hub]);
  }

  private _scheduleReconnect(hub: string, retryCount: number = 0): void {
    if (this._reconnectTimeouts[hub]) {
      clearTimeout(this._reconnectTimeouts[hub]);
    }

    if (this.MAX_RETRY_ATTEMPTS && retryCount >= this.MAX_RETRY_ATTEMPTS) {
      console.error(`Max retry attempts reached for hub ${hub}`);
      return;
    }

    const delay = Math.min(
      this.INITIAL_RETRY_DELAY * Math.pow(2, retryCount),
      this.MAX_RETRY_DELAY
    );

    this._reconnectTimeouts[hub] = setTimeout(() => {
      if (this._authenticationService.isLoggedIn) {
        this._createHub(hub).catch(() => {
          this._scheduleReconnect(hub, retryCount + 1);
        });
      }
    }, delay);
  }

  public ngOnDestroy(): void {
    // Cleanup all connections and subscriptions
    Object.keys(this._connections).forEach((hub) => {
      const connection = this._connections[hub].value;
      if (connection) {
        connection.stop();
      }
      this._subscriptions[hub]?.unsubscribe();
      if (this._reconnectTimeouts[hub]) {
        clearTimeout(this._reconnectTimeouts[hub]);
      }
    });
  }
}
